import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TooltipModule } from 'primeng/tooltip';
import { PUITranslatePipe } from '../pipes/p-ui-translate.pipe';

interface UserIcon {
    icon: string;
    value: number;
    label: string;
    color: string;
}

interface User {
    username: string;
    icons: UserIcon[];
}

@Component({
    selector: 'platform-fuzzy-user-search',
    standalone: true,
    imports: [CommonModule, FormsModule, InputTextModule, RadioButtonModule, TooltipModule, PUITranslatePipe],
    template: `
        <div class="user-search-container h-full">
            <div class="search-bar">
                <span class="p-input-icon-left">
                    <i class="pi pi-search"></i>
                    <input
                        type="text"
                        class="w-full"
                        pInputText
                        placeholder="{{ 'default-cm-searchUser' | pTranslate }}"
                        [(ngModel)]="searchQuery"
                        (input)="filterUsers()"
                    />
                </span>
            </div>
            <div class="title" [ngStyle]="{ color: appliedStyle.titleColor }">
                {{ title | pTranslate: 'All Users' }}
            </div>
            <div class="user-list">
                @if (filteredUsers.length === 0) {
                    <p>
                        {{ 'default-cm-noUsersFound' | pTranslate: 'No users found' }}
                    </p>
                }
                @for (user of filteredUsers; track user) {
                    <div
                        class="user-item cursor-pointer"
                        (click)="selectUser(user)"
                        (keydown.enter)="selectUser(user)"
                        tabindex="0"
                        [ngStyle]="{
                            background:
                                user === selectedUser ? appliedStyle.selectedItemBgColor : appliedStyle.itemBgColor,
                            color: user === selectedUser ? appliedStyle.selectedItemColor : appliedStyle.itemColor,
                        }"
                    >
                        <div class="item-content">
                            <div class="user-info">
                                <p-radioButton name="user" [value]="user" [(ngModel)]="selectedUser"></p-radioButton>
                                <span class="user-email">{{ user.username }}</span>
                            </div>
                            <div class="user-info">
                                @for (icon of user.icons; track icon; let i = $index) {
                                    <div
                                        class="icon-container"
                                        tooltipPosition="bottom"
                                        [pTooltip]="icon?.label | pTranslate: icon?.label"
                                    >
                                        <i
                                            [class]="icon.icon"
                                            [ngStyle]="{
                                                color: icon.color,
                                                fontSize: '1rem',
                                            }"
                                        ></i>
                                        <span class="icon-value">{{
                                            icon.value < 10 && icon.value >= 0 ? '0' + icon.value : icon.value
                                        }}</span>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                }
            </div>
        </div>
    `,
    styles: [
        `
            .user-search-container {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                flex: 1;
                padding: 0.75rem;
            }

            .title {
                padding-top: 4px;
                font-weight: 500;
                padding-bottom: 4px;
                font-size: 0.75rem;
            }

            .search-bar {
                width: 100%;
            }

            .p-input-icon-left {
                width: 100%;
            }

            .user-list {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                overflow-y: scroll;
            }

            .item-content {
                display: flex;
                width: 100%;
                gap: 0.5rem;
                justify-content: space-between;
                align-items: center;
            }

            .user-item {
                display: flex;
                align-items: center;
                height: 42px;
                padding: 0.75rem 1rem 0.75rem 1rem;
                border-radius: 0.5rem;
                transition: background-color 0.3s;
            }

            .user-item:hover {
                background-color: #e9ecef;
            }

            .user-email {
                flex-grow: 1;
                font-size: 0.9rem;
                font-weight: 400;
            }

            .user-info {
                display: flex;
                justify-content: space-between;
                gap: 0.5rem;
            }

            .icon-container {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 0.5rem;
                font-size: 0.8rem;
                border: 1px solid #d1e0f6;
                border-radius: 1.5em;
                padding: 0.25rem 0.625rem 0.25rem 0.625rem;
            }

            .icon-value {
                font-weight: 500;
                font-size: 0.75rem;
            }
        `,
    ],
})
export class FuzzyUserSearchComponent implements OnInit {
    @Input() users: User[] = [];
    @Input() title: string = 'default-cm-users-list-title';
    @Input() appliedStyle: StyleObject = {
        selectedItemColor: '#101828',
        selectedItemBgColor: '#6781a6',
        titleColor: '#425b80',
        itemColor: '#6781a6',
        itemBgColor: '#F9FBFE',
    };
    @Output() onSelect: EventEmitter<User> = new EventEmitter<User>();
    searchQuery: string = '';
    @Input() selectedUser: User | null = null;
    filteredUsers: User[] = [];

    ngOnInit() {
        this.filteredUsers = this.users;
    }

    filterUsers() {
        const query = this.searchQuery.toLowerCase();
        this.filteredUsers = this.users.filter((user) => user.username.toLowerCase().includes(query));
    }

    selectUser(user: User) {
        this.selectedUser = user;
        this.onSelect.emit(user);
    }
}

interface StyleObject {
    itemBgColor: string;
    itemColor: string;
    titleColor: string;
    selectedItemBgColor: string;
    selectedItemColor: string;
}
